import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ChevronLeft, Search } from 'lucide-react';

const FAQ_DATA = [
  {
    category: "Essai gratuit",
    questions: [
      {
        q: "Comment fonctionne l'essai gratuit ?",
        keywords: ["essai", "gratuit", "tester", "test", "découvrir", "7 jours"],
        a: "L'essai gratuit vous donne 7 jours et 10 actions pour découvrir toutes les fonctionnalités de SmartFacture AI. Aucune carte bancaire n'est requise. Vous pouvez scanner des documents, créer des factures et simuler l'envoi PPF/PDP."
      },
      {
        q: "Que se passe-t-il après l'essai ?",
        keywords: ["après", "fin", "expire", "terminé", "expiration"],
        a: "Après l'essai, vos données restent accessibles en lecture. Pour continuer à créer des factures, il suffit de souscrire à un abonnement (Starter à 14€/mois, Pro à 29€/mois ou Expert à 49€/mois)."
      }
    ]
  },
  {
    category: "Abonnements & prix",
    questions: [
      {
        q: "Quels sont les tarifs ?",
        keywords: ["tarif", "prix", "coût", "combien", "abonnement", "plan", "offre"],
        a: "Nous proposons 3 plans : Starter (14€/mois, 30 factures), Pro (29€/mois, illimité) et Expert (49€/mois, avec e-reporting et multi-utilisateurs). Un essai gratuit de 7 jours est disponible."
      },
      {
        q: "Comment utiliser un code promo ?",
        keywords: ["promo", "code", "réduction", "remise", "LANCEMENT26", "coupon"],
        a: "Sur la page Tarifs, entrez votre code promo (ex: LANCEMENT26 pour -20% sur le premier mois) dans le champ prévu, puis cliquez sur \"Appliquer\". La réduction sera appliquée automatiquement lors du paiement."
      },
      {
        q: "Puis-je annuler mon abonnement ?",
        keywords: ["annuler", "résilier", "arrêter", "résiliation", "annulation"],
        a: "Oui, vous pouvez résilier à tout moment depuis votre espace client. La résiliation prend effet à la fin de la période en cours. Conformément à nos CGU, aucun remboursement n'est accordé après activation."
      }
    ]
  },
  {
    category: "Conformité 2026",
    questions: [
      {
        q: "Qu'est-ce que la réforme e-facturation 2026 ?",
        keywords: ["réforme", "2026", "obligation", "loi", "e-facturation", "obligatoire"],
        a: "À partir du 1er septembre 2026, toutes les entreprises françaises doivent pouvoir recevoir des factures électroniques au format Factur-X. SmartFacture AI vous prépare automatiquement à cette obligation."
      },
      {
        q: "Suis-je conforme avec SmartFacture ?",
        keywords: ["conforme", "conformité", "Factur-X", "norme", "valide"],
        a: "Oui ! SmartFacture AI génère des factures au format Factur-X (PDF/A-3 + XML) avec toutes les mentions obligatoires (SIREN, TVA, etc.). Un score de conformité vous indique le niveau de chaque facture."
      },
      {
        q: "Qu'est-ce que PPF/PDP ?",
        keywords: ["PPF", "PDP", "portail", "plateforme", "dématérialisation"],
        a: "Le PPF (Portail Public de Facturation) est la plateforme de l'État pour l'échange de factures électroniques. Une PDP (Plateforme de Dématérialisation Partenaire) est une alternative privée agréée. SmartFacture vous permet de simuler l'envoi sur les deux."
      }
    ]
  },
  {
    category: "Fonctionnalités",
    questions: [
      {
        q: "Comment fonctionne le scanner IA ?",
        keywords: ["scanner", "scan", "OCR", "photo", "document", "numériser"],
        a: "Prenez en photo ou importez un document (facture, reçu, justificatif). Notre IA analyse automatiquement le contenu, extrait les données (montant, TVA, fournisseur) et peut convertir le document en facture conforme."
      },
      {
        q: "Comment créer une facture ?",
        keywords: ["créer", "facture", "nouvelle", "faire", "générer"],
        a: "Depuis votre tableau de bord, cliquez sur \"Nouvelle facture\". Remplissez les informations client, ajoutez vos lignes de facturation, et SmartFacture vérifie automatiquement la conformité avant l'export."
      },
      {
        q: "Mes données sont-elles sécurisées ?",
        keywords: ["sécurité", "données", "privé", "RGPD", "confidentialité", "sécurisé"],
        a: "Absolument. Vos données sont chiffrées, hébergées sur des serveurs en France et conformes au RGPD. Aucune facture n'est transmise à l'administration sans votre validation explicite."
      }
    ]
  },
  {
    category: "Parrainage",
    questions: [
      {
        q: "Comment fonctionne le parrainage ?",
        keywords: ["parrainage", "parrain", "filleul", "inviter", "ami"],
        a: "Partagez votre lien de parrainage (disponible dans votre dashboard). Quand votre contact s'abonne, vous recevez tous les deux 1 mois gratuit ! Maximum 12 mois offerts par parrain."
      }
    ]
  }
];

const SupportAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('categories');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleOpen = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([{
        type: 'bot',
        text: "Bonjour ! Je suis l'assistant SmartFacture. Comment puis-je vous aider ?"
      }]);
    }
  };

  const findAnswer = (query) => {
    const lower = query.toLowerCase();
    let bestMatch = null;
    let bestScore = 0;

    for (const cat of FAQ_DATA) {
      for (const faq of cat.questions) {
        let score = 0;
        for (const kw of faq.keywords) {
          if (lower.includes(kw.toLowerCase())) score += 1;
        }
        if (score > bestScore) {
          bestScore = score;
          bestMatch = faq;
        }
      }
    }

    return bestScore > 0 ? bestMatch.a : null;
  };

  const handleSend = () => {
    const query = inputValue.trim();
    if (!query) return;

    setMessages(prev => [...prev, { type: 'user', text: query }]);
    setInputValue('');

    const answer = findAnswer(query);

    setTimeout(() => {
      if (answer) {
        setMessages(prev => [...prev, { type: 'bot', text: answer }]);
      } else {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: "Je n'ai pas trouvé de réponse précise. Vous pouvez nous contacter par email à support@smartfacture-ai.com pour une aide personnalisée, ou choisir une question ci-dessous.",
          showCategories: true
        }]);
      }
    }, 500);

    setView('chat');
  };

  const handleQuestionClick = (faq) => {
    setMessages(prev => [
      ...prev,
      { type: 'user', text: faq.q },
      { type: 'bot', text: faq.a }
    ]);
    setView('chat');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setIsOpen(false)} />
      )}

      {/* Chat Panel */}
      <div className={`fixed bottom-32 right-4 sm:right-6 z-50 transition-all duration-300 ease-out ${
        isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-[340px] sm:w-[380px] max-h-[500px] flex flex-col overflow-hidden" data-testid="support-assistant">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {view !== 'categories' && (
                  <button onClick={() => setView('categories')} className="text-white/80 hover:text-white">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <div>
                  <h3 className="text-white font-semibold text-sm">Assistant SmartFacture</h3>
                  <p className="text-emerald-100 text-xs">Réponses instantanées</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '340px' }}>
            {view === 'categories' && (
              <>
                <div className="space-y-2">
                  {FAQ_DATA.map((cat, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setSelectedCategory(cat); setView('questions'); }}
                      className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-emerald-50 hover:border-emerald-200 border border-slate-100 transition-colors"
                      data-testid={`faq-category-${idx}`}
                    >
                      <p className="font-medium text-slate-800 text-sm">{cat.category}</p>
                      <p className="text-xs text-slate-500">{cat.questions.length} question{cat.questions.length > 1 ? 's' : ''}</p>
                    </button>
                  ))}
                </div>
              </>
            )}

            {view === 'questions' && selectedCategory && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{selectedCategory.category}</p>
                {selectedCategory.questions.map((faq, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuestionClick(faq)}
                    className="w-full text-left p-3 rounded-xl bg-slate-50 hover:bg-emerald-50 border border-slate-100 hover:border-emerald-200 transition-colors"
                  >
                    <p className="text-sm text-slate-700">{faq.q}</p>
                  </button>
                ))}
              </div>
            )}

            {view === 'chat' && (
              <>
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.type === 'user'
                        ? 'bg-emerald-500 text-white rounded-br-md'
                        : 'bg-slate-100 text-slate-700 rounded-bl-md'
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 p-3 flex-shrink-0">
            <div className="flex gap-2">
              <input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Posez votre question..."
                className="flex-1 text-sm border border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-emerald-400 text-slate-700 bg-white"
                data-testid="support-input"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white rounded-xl px-3 transition-colors"
                data-testid="support-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-2">
              Ou contactez-nous : support@smartfacture-ai.com
            </p>
          </div>
        </div>
      </div>

      {/* Floating Button */}
      <button
        onClick={isOpen ? () => setIsOpen(false) : handleOpen}
        className={`fixed bottom-20 right-4 sm:right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-300 ${
          isOpen
            ? 'bg-slate-800 hover:bg-slate-700'
            : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
        }`}
        style={{ boxShadow: isOpen ? '0 4px 20px rgba(0,0,0,0.15)' : '0 4px 20px rgba(59,130,246,0.4)' }}
        data-testid="support-assistant-btn"
      >
        {isOpen ? <X className="h-5 w-5 text-white" /> : <MessageCircle className="h-5 w-5 text-white" />}
        {!isOpen && <span className="text-white font-medium text-sm hidden sm:inline">Aide</span>}
      </button>
    </>
  );
};

export default SupportAssistant;
